/**
 * MED-781: Horizontal-scroll Needs Attention rail.
 *
 * - AC5: Section title with count badge.
 * - AC6/AC7: cards in a single horizontal row, ~4 visible at desktop, scroll
 *   for additional. Stays horizontal at all breakpoints (no wrap).
 * - AC15: When empty, render the "You're all caught up" empty state in place
 *   of cards. Per Q8 resolution the count badge is hidden when zero.
 */

import { Box, Group, Stack, Text, Title, Skeleton, Badge, ThemeIcon } from '@mantine/core';
import { Check } from 'react-feather';
import { NeedsAttentionCard } from './NeedsAttentionCard';
import type { NeedsAttentionItem } from './needsAttentionTypes';

interface NeedsAttentionRailProps {
  items: NeedsAttentionItem[];
  isLoading: boolean;
  onCardCta: (item: NeedsAttentionItem) => void;
}

export function NeedsAttentionRail({ items, isLoading, onCardCta }: NeedsAttentionRailProps) {
  const showBadge = !isLoading && items.length > 0;

  return (
    <Stack gap="sm">
      <Group gap={8} align="center">
        <Title order={4}>Needs Attention</Title>
        {showBadge && (
          <Badge size="lg" circle variant="filled" color="dark">
            {items.length}
          </Badge>
        )}
      </Group>

      {/* MED-1394: a card-sized placeholder, so the page does not jump on load. */}
      {isLoading ? (
        <Skeleton height={160} width={320} radius={12} />
      ) : items.length === 0 ? (
        <AllCaughtUp />
      ) : (
        <Box
          style={{
            display: 'flex',
            gap: 16,
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x mandatory',
            paddingBottom: 8,
            // Keep horizontal even on small screens (AC7).
            flexWrap: 'nowrap',
          }}
        >
          {items.map(item => (
            <NeedsAttentionCard key={item.id} item={item} onCta={onCardCta} />
          ))}
        </Box>
      )}
    </Stack>
  );
}

function AllCaughtUp() {
  return (
    <Group
      gap="sm"
      p="lg"
      align="center"
      style={{
        background: 'var(--mantine-color-gray-0)',
        borderRadius: 8,
        border: '1px dashed var(--mantine-color-gray-3)',
      }}
    >
      <ThemeIcon size={36} radius="xl" variant="light" color="medleyGreen">
        <Check size={18} />
      </ThemeIcon>
      <Text size="sm" fw={600}>
        You're all caught up
      </Text>
    </Group>
  );
}
